import { createCRUDHook } from './createCRUDHook';
import {
  FilterOptionModel,
  JsonPatch,
  LoggedInUserTeamDto,
  StaffDto,
  StaffPaidTimeDto,
  StaffRoleDto,
} from 'utils/models';
import {
  CHECK_OLD_PASSWORD,
  CHECK_USERNAME_EXIST,
  FILTERED_ENTITIES,
  GET_USER_PERMISSION,
  TLKP_STAFF,
  STAFF_ROLE,
  GET_LOGGED_IN_USER_TEAM_DETAILS,
  TBL_STAFF_PAID_TIME,
  GET_STAFF_PAID_TIME,
  ROLLOVER,
  RESET_ALL,
  RESET,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';
import { AxiosResponse } from 'axios';

interface StaffCustomOperations {
  GetStaffPermissionById: (staffId: number) => Promise<any>;
  CheckIsUsernameExistOrNot: (username: string) => Promise<boolean>;
  CheckOldPassword: (userId: number, hash: string) => Promise<boolean>;
  getLoggedInUserTeamDetails: (staffId: number) => Promise<LoggedInUserTeamDto>;
  getStaffRoleDataByFilteredEntities: (
    filteredEntities: FilterOptionModel
  ) => Promise<StaffRoleDto[]>;

  getStaffPaidTimeData: () => Promise<StaffPaidTimeDto[]>;
  getStaffPaidTimeDataById: (id: number) => Promise<StaffPaidTimeDto>;
  addStaffPaidTimeData: (param: StaffPaidTimeDto) => Promise<StaffPaidTimeDto>;
  updateStaffPaidTimeData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deleteStaffPaidTimeData: (id: string) => Promise<boolean>;
  getStaffPaidTimeDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<StaffPaidTimeDto[]>;
  getStaffPaidTimeByStartAndEndDate: (
    startDate: string,
    endDate: string,
    staffId?: number
  ) => Promise<StaffPaidTimeDto[]>;

  addStaffRoleData: (param: StaffRoleDto) => Promise<StaffRoleDto>;
  deleteStaffRoleData: (id: number) => Promise<AxiosResponse>;

  rolloverAllStaffLeaves: () => Promise<AxiosResponse>;
  rolloverLeavesByStaffId: (id: number) => Promise<AxiosResponse>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  StaffDto,
  StaffDto[],
  StaffCustomOperations
>({
  entityName: 'Staff',
  endpoint: TLKP_STAFF,

  customOperations: (auth) => ({
    GetStaffPermissionById: async (staffId: number) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TLKP_STAFF}${GET_USER_PERMISSION}?id=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    CheckIsUsernameExistOrNot: async (username: string) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TLKP_STAFF}${CHECK_USERNAME_EXIST}?username=${username}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    CheckOldPassword: async (userId: number, hash: string) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TLKP_STAFF}${CHECK_OLD_PASSWORD}?userId=${userId}&hash=${hash}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getLoggedInUserTeamDetails: async (staffId: number) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TLKP_STAFF}${GET_LOGGED_IN_USER_TEAM_DETAILS}?staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getStaffRoleDataByFilteredEntities: async (
      filteredEntities: FilterOptionModel
    ) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          STAFF_ROLE +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addStaffRoleData: async (param: StaffRoleDto) => {
      try {
        const res = await auth.makeAuthenticatedPost(STAFF_ROLE, param);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deleteStaffRoleData: async (id: number) => {
      try {
        const res = await auth.makeAuthenticatedDelete(`${STAFF_ROLE}/${id}`);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getStaffPaidTimeData: async () => {
      try {
        const res = await auth.makeAuthenticatedGet(TBL_STAFF_PAID_TIME);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getStaffPaidTimeDataById: async (id: number) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TBL_STAFF_PAID_TIME}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addStaffPaidTimeData: async (param: StaffPaidTimeDto) => {
      try {
        const res = await auth.makeAuthenticatedPost(
          TBL_STAFF_PAID_TIME,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateStaffPaidTimeData: async (param: JsonPatch[], id: number) => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${TBL_STAFF_PAID_TIME}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deleteStaffPaidTimeData: async (id: string) => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${TBL_STAFF_PAID_TIME}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },

    getStaffPaidTimeDataByFE: async (filteredEntities: FilterOptionModel) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_STAFF_PAID_TIME +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getStaffPaidTimeByStartAndEndDate: async (
      startDate: string,
      endDate: string,
      staffId?: number
    ) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_STAFF_PAID_TIME +
            GET_STAFF_PAID_TIME +
            `?startDate=${startDate}&endDate=${endDate}&staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    rolloverAllStaffLeaves: async () => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TLKP_STAFF + ROLLOVER + RESET_ALL
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    rolloverLeavesByStaffId: async (id: number) => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TLKP_STAFF + ROLLOVER + RESET + `/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const StaffContext = Context;
export const useStaffManager = useManager;
export const useStaff = useConsumer;
